import React from 'react';
import {
  MultiSelect, NumberInput, Select, Stack, Switch,
} from '@mantine/core';
import { PuckColorFieldWithPalette } from '../../../puck/fields/createPuckColorField';
import InspectorRow from '../../../puck/fields/InspectorRow';
import { useAdvancedSectionOpen } from '../../../puck/fields/puckSectionField';
import { useSelectedItemProps } from '../../../puck/fields/useSelectedItemProps';
import { useThemeLockedProp } from '../../../puck/fields/themedFieldValues';
import StyleDefaultMark from '../../../puck/fields/StyleDefaultMark';
import { storedTableDesign, withTableStyleDefaults } from './tableStyles';
import { plainCellText } from './tableLayout';
import { isSortableColumn } from './tableInteractions';
import styles from './TableStyleSettings.module.css';

const STICKY_COLUMNS_OPTIONS = [
  { value: '0', label: 'Aucune' },
  { value: '1', label: 'La première' },
  { value: '2', label: 'Les 2 premières' },
  { value: '3', label: 'Les 3 premières' },
];

const MOBILE_LAYOUT_OPTIONS = [
  { value: 'scroll', label: 'Défiler' },
  { value: 'cards', label: 'Passer en cartes' },
];

const LAYOUT_OPTIONS = [
  { value: 'table', label: 'Tableau' },
  { value: 'cards', label: 'Cartes' },
];

const CARD_HEADER_OPTIONS = [
  { value: 'firstCell', label: 'Une carte par ligne' },
  { value: 'columnTitle', label: 'Une carte par colonne' },
];

const BORDER_SCOPE_OPTIONS = [
  { value: 'all', label: 'Toutes' },
  { value: 'rows', label: 'Entre les lignes' },
  { value: 'none', label: 'Aucune' },
];

const BORDER_STYLE_OPTIONS = [
  { value: 'solid', label: 'Plein' },
  { value: 'dashed', label: 'Tirets' },
  { value: 'dotted', label: 'Pointillés' },
  { value: 'double', label: 'Double' },
  // Conservée : des tableaux enregistrés portent cette valeur, et un select
  // sans option correspondante s'afficherait vide.
  { value: 'none', label: 'Aucune' },
];

const SIZE_OPTIONS = [
  { value: 'default', label: 'Normale' },
  { value: 'middle', label: 'Moyenne' },
  { value: 'small', label: 'Compacte' },
];

/**
 * Réglages fins du style du tableau, dans la section « Réglages avancés » (la
 * galerie, elle, reste dans « Style »).
 *
 * Les réglages fins étaient une quinzaine de champs déroulés sous la galerie,
 * ce qui noyait les deux listes qui comptent (colonnes, lignes). Ils sont
 * rendus ici plutôt que déclarés en `objectFields` : Puck n'a aucun moyen de
 * regrouper des champs sans les regrouper *aussi* dans la donnée, et renommer
 * des props de `design` est exclu (README §4, données legacy).
 *
 * Écritures : un patch de `design` par réglage, via le même dispatch que la
 * galerie.
 */
/** Libellé de colonne sans son HTML (il s'édite en texte riche au canvas). */
const plainText = (value) => String(value || '').replace(/<[^>]+>/g, '').trim();

export default function TableStyleSettings() {
  const { itemProps, updateItemProps } = useSelectedItemProps();
  const design = storedTableDesign(itemProps.design);
  const resolved = withTableStyleDefaults(design);

  const set = (key, value) => updateItemProps({ design: { ...design, [key]: value } });
  const isLocked = useThemeLockedProp('table');

  const color = (key, label) => !isLocked(key) && (
    <InspectorRow label={label}>
      <PuckColorFieldWithPalette
        label={null}
        value={resolved[key]}
        onChange={(next) => set(key, next)}
        allowTransparent
      />
    </InspectorRow>
  );

  const bool = (key, label, { description } = {}) => !isLocked(key) && (
    <StyleDefaultMark propKey={key}>
      <Switch
        size="xs"
        label={label}
        description={description}
        checked={!!resolved[key]}
        onChange={(event) => set(key, event.currentTarget.checked)}
      />
    </StyleDefaultMark>
  );

  const select = (key, label, data) => !isLocked(key) && (
    <StyleDefaultMark propKey={key}>
      <Select
        size="xs"
        label={label}
        data={data}
        value={resolved[key]}
        onChange={(next) => next != null && set(key, next)}
        comboboxProps={{ withinPortal: true }}
        allowDeselect={false}
      />
    </StyleDefaultMark>
  );

  const number = (key, label, { min, max }) => !isLocked(key) && (
    <NumberInput
      size="xs"
      label={label}
      min={min}
      max={max}
      value={Number(resolved[key]) || 0}
      onChange={(next) => set(key, Number(next) || 0)}
    />
  );

  const columnOptions = (columns) => columns.map((column) => ({
    value: column.key,
    label: plainText(column.label) || column.key,
  }));

  const isAdvancedOpen = useAdvancedSectionOpen();
  const isCards = resolved.layout === 'cards';
  const isTable = !isCards;

  /* Contenu monté seulement à l'ouverture de la section. Ce champ se
     redessine à CHAQUE changement du document — une frappe dans une cellule
     comprise. Garder une vingtaine de contrôles Mantine montés, dont six
     sélecteurs de couleur avec leur popover, se payait à chaque touche. */
  if (!isAdvancedOpen) return null;

  // Les colonnes que le tableau acceptera de trier. Les rôles des lignes
  // (titres, totaux) ne se lisent qu'avec la table : toutes comptent ici.
  const rowTexts = (itemProps.rows || []).map((row) => (row.cells || []).map((cell) => plainCellText(cell?.value)));
  const sortableColumns = (itemProps.columns || []).filter((column, index) => (
    isSortableColumn(column, index, rowTexts, rowTexts.map(() => 'item'))
  ));

  return (
    <Stack gap={12}>
      {select('layout', 'Disposition', LAYOUT_OPTIONS)}
      {/* Le découpage de la carte : par ligne (chaque ligne devient une
          carte) ou par colonne (grille tarifaire, la première colonne
          fournit les libellés). */}
      {isCards && select('cardHeader', 'Découpage des cartes', CARD_HEADER_OPTIONS)}

      {bool('showHeader', 'Afficher l’en-tête')}
      {color('headerBgColor', "Couleur de fond de l'en-tête")}
      {color('headerTextColor', "Couleur du texte de l'en-tête")}
      {bool('headerUppercase', 'En-tête en petites capitales')}
      {bool('firstColumnEmphasis', 'Première colonne en libellé')}

      {select('borderScope', 'Bordures', BORDER_SCOPE_OPTIONS)}
      {color('borderColor', 'Couleur des bordures')}
      {number('borderWidth', 'Taille de la bordure (px)', { min: 0, max: 8 })}
      {select('borderStyle', 'Style de bordure', BORDER_STYLE_OPTIONS)}
      {number('cornerRadius', 'Arrondi des angles (px)', { min: 0, max: 32 })}

      {/* Densité : elle règle l'espacement des cellules d'un `<table>`.
          En cartes, ce sont les cartes qui portent leur padding. */}
      {isTable && select('size', 'Densité', SIZE_OPTIONS)}
      {bool('isStripped', 'Lignes alternées')}
      {color('rowEvenBgColor', 'Couleur des lignes paires')}
      {color('strippedColor', 'Couleur des lignes impaires')}
      {bool('highlightOnHover', 'Surligner la ligne au survol')}
      {resolved.highlightOnHover && !isLocked('highlightOnHoverColor') && (
        <>
          {color('highlightOnHoverColor', 'Couleur au survol')}
          {/* L'aperçu emprunte les couleurs du tableau — en-tête,
              alternance, bordures : une pastille de couleur seule ne
              dit pas si le survol se voit SUR ce tableau-là. */}
          <div
            className={styles.hoverPreview}
            style={{
              '--preview-hover': resolved.highlightOnHoverColor || undefined,
              '--preview-border': resolved.borderColor,
              '--preview-even': resolved.rowEvenBgColor === 'transparent'
                ? 'transparent' : resolved.rowEvenBgColor,
              '--preview-odd': resolved.isStripped ? resolved.strippedColor : 'transparent',
            }}
          >
            {resolved.showHeader && (
              <div
                className={styles.hoverPreviewHead}
                style={{
                  background: resolved.headerBgColor,
                  color: resolved.headerTextColor,
                  textTransform: resolved.headerUppercase ? 'uppercase' : undefined,
                }}
              >
                Aperçu
              </div>
            )}
            <div className={styles.hoverPreviewRow} data-parity="even">Survolez cette ligne</div>
            <div className={styles.hoverPreviewRow} data-parity="odd">puis celle-ci</div>
          </div>
        </>
      )}

      {/* Défilement : sans `<table>`, ni en-tête ni colonne à figer. Une
          hauteur max fait défiler le tableau sur lui-même : en-tête fixe et
          colonnes figées marchent alors ensemble. */}
      {isTable && number('maxHeight', 'Hauteur max (px, 0 = automatique)', { min: 0, max: 2000 })}
      {isTable && !isLocked('minColumnWidth') && (
        <NumberInput
          size="xs"
          label="Largeur min. d’une colonne (px)"
          description="En dessous, le tableau défile plutôt que de couper ses mots."
          min={0}
          max={600}
          value={resolved.minColumnWidth ?? 100}
          onChange={(next) => set('minColumnWidth', Number(next) || 0)}
        />
      )}
      {isTable && bool('stickyHeader', 'En-tête fixe au défilement', {
        description: Number(resolved.maxHeight) > 0
          ? 'Il reste en haut du tableau pendant qu’il défile.'
          : 'Sans hauteur max, il colle à la page et le tableau ne défile plus latéralement.',
      })}
      {isTable && !isLocked('stickyColumns') && (
        <Select
          size="xs"
          label="Colonnes figées à gauche"
          data={STICKY_COLUMNS_OPTIONS.slice(0, Math.max((itemProps.columns?.length || 1), 1))}
          value={String(Number(resolved.stickyColumns) || 0)}
          onChange={(next) => next != null && set('stickyColumns', Number(next))}
          comboboxProps={{ withinPortal: true }}
          allowDeselect={false}
        />
      )}
      {isTable && !isLocked('mobileLayout') && (
        <Select
          size="xs"
          label="Sur petit écran"
          data={MOBILE_LAYOUT_OPTIONS}
          value={resolved.mobileLayout || 'scroll'}
          onChange={(next) => next != null && set('mobileLayout', next)}
          comboboxProps={{ withinPortal: true }}
          allowDeselect={false}
        />
      )}

      {/* Lecture interactive : sur le site et l'aperçu, jamais au canvas
          (cf. tableInteractions). */}
      {isTable && bool('sortable', 'Tri par colonne', {
        description: 'Un clic sur un en-tête trie les lignes ; titres et totaux restent à leur place. '
          + 'Pas de tri quand chaque groupe a les mêmes lignes (BPU par poste).',
      })}
      {isTable && resolved.sortable && !isLocked('sortColumns') && (
        <MultiSelect
          size="xs"
          label="Colonnes triables"
          description="Les colonnes d’images ou sans valeur ne se trient jamais."
          placeholder={resolved.sortColumns?.length ? '' : 'Toutes'}
          data={columnOptions(sortableColumns)}
          value={resolved.sortColumns || []}
          onChange={(next) => set('sortColumns', next)}
          comboboxProps={{ withinPortal: true }}
          clearable
        />
      )}
      {bool('searchable', 'Champ de recherche')}
      {!isLocked('filterColumn') && (
        <Select
          size="xs"
          label="Filtre sur la colonne"
          description="Sur un tableau à groupes (postes, zones…), on filtre par groupe."
          placeholder="Aucun"
          data={columnOptions(itemProps.columns || [])}
          value={resolved.filterColumn || null}
          onChange={(next) => set('filterColumn', next || '')}
          comboboxProps={{ withinPortal: true }}
          clearable
        />
      )}
      {isTable && itemProps.tableName && bool('collapsibleGroups', 'Groupes repliables', {
        description: 'Les lignes-titres (zones, postes, sections) se replient d’un clic.',
      })}
      {isTable && bool('crosshairHover', 'Surligner la colonne au survol')}
      {bool('footer', 'Afficher le pied de tableau')}
    </Stack>
  );
}
